/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

import { getLevelMetadata, getLevelById } from "../../../api";

export interface Level {
  id: number;
  name: string;
  description: string;
  status: "locked" | "in_progress" | "passed";
  rankName: string;
}

interface LevelContextType {
  levels: Level[];
  levelsLoading: boolean;
  error: Error | null;
  refetchLevels: () => Promise<void>;
  isLoadingLevelDetails: boolean;
  levelDetails: Record<number, Level>;
}

const LevelContext = createContext<LevelContextType | undefined>(undefined);

interface LevelContextProviderProps {
  children: ReactNode;
}

export const LevelContextProvider: React.FC<LevelContextProviderProps> = ({ children }) => {
  const [levels, setLevels] = useState<Level[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingLevelDetails, setIsLoadingLevelDetails] = useState<boolean>(false);
  const [levelDetails, setLevelDetails] = useState<Record<number, Level>>({});
  const [error, setError] = useState<Error | null>(null);

  // if the user has levels that are locked, we shouldn't show them or care about them in anyway
  const filterAvailableLevels = (levels: Level[]) => {
    return levels.filter(level => level.status !== "locked");
  };

  const fetchLevels = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const levelData = await getLevelMetadata();
      setLevels(filterAvailableLevels(levelData));
    } catch (err) {
      console.error("Failed to fetch level metadata:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch level metadata"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevelDetails = async () => {
    if (levels.length === 0) return;

    setIsLoadingLevelDetails(true);

    try {
      // Fetch level metadata for each level they have available
      levels.forEach(level => {
        getLevelById(level.id)
          .then(data => {
            setLevelDetails(prev => ({ ...prev, [level.id]: data }));
          })
          .catch(err => console.error(`Error fetching level ${level.id}:`, err));
      });
    } catch (error) {
      console.error("Error fetching level data:", error);
      setError(error instanceof Error ? error : new Error("Failed to fetch level details"));
    } finally {
      setIsLoadingLevelDetails(false);
    }
  };

  useEffect(() => {
    if (levels.length > 0 && !isLoading) {
      fetchLevelDetails();
    }
  }, [levels, isLoading]);

  const value: LevelContextType = {
    levels,
    levelsLoading: isLoading,
    error,
    refetchLevels: fetchLevels,
    isLoadingLevelDetails,
    levelDetails,
  };

  return <LevelContext.Provider value={value}>{children}</LevelContext.Provider>;
};

export const useLevelContext = (): LevelContextType => {
  const context = useContext(LevelContext);
  if (context === undefined) {
    throw new Error("useLevelContext must be used within a LevelContextProvider");
  }
  return context;
};
